import * as React from 'react'
import { QueryKey, useQuery } from '@tanstack/react-query'

import {
  Block,
  BlockChange,
  BlockType,
  CommonBlocksCompoundIndexes,
  isTypeForBlockWithChildren,
} from '@basehub/data/replicache/index'

import { invariant } from '@epic-web/invariant'
import { TreeManager, getTreeManager } from '@basehub/utils/tree/index'

import { useEffect } from 'react'
import { WorkersContext, useRepoWorkers } from '~/workers/provider'
import { idToSourceBlockId } from '~/replicache/schema/repo-space/block/mutators/copy-on-write'
import { TreeCompoundIndex } from '@basehub/utils/tree/index'
import { hashJson } from '@basehub/utils/hash'

type UseDiffProps = {
  /**
   * if `false`, nothing to compare to. `undefined` is for loading...
   */
  treeOld: TreeManager<Block, CommonBlocksCompoundIndexes> | false | undefined
  /**
   * if `null`, compare to working tree. `undefined` is for loading...
   */
  treeNew: TreeManager<Block, CommonBlocksCompoundIndexes> | undefined
  /**
   * if `false`, nothing to compare to. `undefined` is for loading...
   */
  treeCommonAncestor:
    | TreeManager<Block, CommonBlocksCompoundIndexes>
    | false
    | undefined
  keyOverride?: QueryKey
  diffType?: 'commit' | 'merge'
}

export const useDiff = ({
  treeOld,
  treeNew,
  treeCommonAncestor,
  keyOverride,
  diffType = 'commit',
}: UseDiffProps) => {
  const { diffWorker } = useRepoWorkers()
  const enabled = Boolean(
    treeNew &&
      (treeOld === false ? true : treeOld) &&
      (treeCommonAncestor === false ? true : treeCommonAncestor)
  )

  const {
    data: diffResult,
    error,
    refetch,
    isInitialLoading: isLoading,
  } = useQuery(
    keyOverride ?? [
      'diff',
      diffType,
      treeOld === false ? false : treeOld?.root.value?.hash,
      treeNew?.root.value.hash,
      treeNew?.root.value.id,
      treeCommonAncestor === false
        ? false
        : treeCommonAncestor?.root.value?.hash,
    ],
    async ({ queryKey }) => {
      return await queryFn({
        treeNew,
        treeOld,
        treeCommonAncestor,
        diffWorker,
        diffType,
        queryKey,
      })
    },
    {
      staleTime: Infinity,
      enabled,
      keepPreviousData: false,
      retry: false,
      cacheTime: 30 * 1000, // 30 seconds after the query becomes unused, it'll be garbage collected
    }
  )

  useEffect(() => {
    if (error) {
      console.error('Error while diffing', error)
    }
  }, [error])

  return React.useMemo(() => {
    if (!diffResult) {
      return { error, enabled }
    }
    return {
      diff: diffResult.diff,
      treeNew: diffResult.treeNew,
      treeOld: diffResult.treeOld,
      treeCommonAncestor: diffResult.treeCommonAncestor,
      error,
      refetch,
      isLoading,
      enabled,
    }
  }, [diffResult, refetch, error, isLoading, enabled])
}

export type UseDiffResult = ReturnType<typeof useDiff>

export function isValidChange(change: BlockChange) {
  return (
    change.status !== 'unmodified' &&
    isTypeForBlockWithChildren(change.type as BlockType)
  )
}

async function queryFn({
  treeNew,
  treeOld,
  treeCommonAncestor,
  diffWorker,
  diffType = 'commit',
  queryKey,
}: Pick<
  UseDiffProps,
  'treeNew' | 'treeOld' | 'treeCommonAncestor' | 'diffType'
> & {
  diffWorker: WorkersContext['diffWorker']
  queryKey: QueryKey
}) {
  invariant(treeNew, 'treeNew is missing')
  invariant(treeOld === false || treeOld, 'treeOld is not declared nor false')
  invariant(
    treeCommonAncestor === false || treeCommonAncestor,
    'treeCommonAncestor is not declared nor false'
  )

  const resultFromWorker = await diffWorker?.getDiffBetweenTrees(
    hashJson(queryKey),
    {
      blockMilestones: null,
      newTreeData: {
        rootBlockId: treeNew.root.value.id,
        nodes: Array.from(treeNew.itemsMap.values()),
      },
      commonTreeData: treeCommonAncestor
        ? {
            rootBlockId: treeCommonAncestor.root.value.id,
            nodes: Array.from(treeCommonAncestor.itemsMap.values()),
          }
        : null,
      oldTreeData: treeOld
        ? {
            rootBlockId: treeOld.root.value.id,
            nodes: Array.from(treeOld.itemsMap.values()),
          }
        : null,
      diffType,
    }
  )

  if (!resultFromWorker) {
    throw new Error('Diff worker did not return a result')
  }

  const itemsMap = new Map(
    resultFromWorker.items.map((item) => [item.key, item])
  )

  const changesTree = getTreeManager<BlockChange, ChangesTreeCompoundIndexed>({
    rootKey: resultFromWorker.root.value.id,
    getKey: (item) => idToSourceBlockId(item),
    getChildrenKeys: (_key, value) => value.children?.map(({ id }) => id) ?? [],
    getValue: (key) => {
      const item = itemsMap.get(key)
      invariant(item, `Change node with ID ${key} must exist`)
      return item.value
    },
    getOrder: (key) => {
      const item = itemsMap.get(key)
      invariant(item, `Change node with ID ${key} must exist`)
      return item.order
    },
    compoundIndexes: {
      conflictsWizard: {
        updateIndex: ({ node, oldNode, currentValue, operation }) => {
          if (
            node.value.status !== 'conflict' &&
            (!oldNode || oldNode.value.status !== 'conflict')
          ) {
            return currentValue ?? new Set<string>()
          }

          const current = currentValue ?? new Set<string>()

          switch (operation) {
            case 'upsert':
              current.add(node.key)
              break
            case 'delete':
              current.delete(node.key)
              break
          }

          if (node.value.status !== 'conflict') {
            current.delete(node.key)
          }

          return current
        },
      },
    },
  })

  itemsMap.clear()
  resultFromWorker.items = []

  return {
    diff: {
      hasChanged: resultFromWorker.changesCount > 0,
      changesTree,
      changesCount: resultFromWorker.changesCount,
      conflictsCount: resultFromWorker.conflictsCount,
    },
    treeNew,
    treeOld,
    treeCommonAncestor,
  }
}

type ChangesTreeCompoundIndexed = {
  conflictsWizard: TreeCompoundIndex<BlockChange, Set<string>>
}
